import { supabase } from './supabase';

export interface BookingDiscount {
  id: string;
  booking_id: string;
  title: string;
  type: 'percentage' | 'fixed';
  amount: number;
  created_at: string;
  updated_at: string;
}

export async function applyDiscount(bookingId: string, name: string, type: 'percentage' | 'fixed', amount: number) {
  if (!bookingId) {
    throw new Error('Booking ID is required');
  }

  if (!name.trim()) {
    throw new Error('Discount name is required');
  }

  if (amount <= 0) {
    throw new Error('Amount must be greater than 0');
  }

  if (type === 'percentage' && amount > 100) {
    throw new Error('Percentage cannot exceed 100%');
  }

  // First, get the current booking to calculate the new total
  const { data: booking, error: bookingError } = await supabase
    .from('bookings')
    .select('subtotal, tax_amount')
    .eq('id', bookingId)
    .single();

  if (bookingError) {
    console.error('Error fetching booking:', bookingError);
    throw new Error('Failed to fetch booking details');
  }

  // Remove any existing discounts from this booking (enforce single discount rule)
  const { error: removeError } = await supabase
    .from('booking_discounts')
    .delete()
    .eq('booking_id', bookingId);

  if (removeError) {
    console.error('Error removing existing discounts:', removeError);
    throw new Error('Failed to remove existing discount. Please try again.');
  }

  // Calculate the discount amount with proper currency rounding
  const discountAmount = Math.round((type === 'percentage' 
    ? (booking.subtotal * amount / 100)
    : amount) * 100) / 100;

  // Insert the discount
  const { data: discount, error: discountError } = await supabase
    .from('booking_discounts')
    .insert([
      {
        booking_id: bookingId,
        title: name.trim(),
        type,
        amount
      }
    ])
    .select()
    .single();

  if (discountError) {
    console.error('Error applying discount:', discountError);
    throw new Error('Failed to apply discount. Please try again.');
  }

  // Update the booking's total amount with proper currency rounding
  const newTotal = Math.round((totalBookingAmount - discountAmount) * 100) / 100;
  const { error: updateError } = await supabase
    .from('bookings')
    .update({
      total_amount: newTotal
    })
    .eq('id', bookingId);

  if (updateError) {
    console.error('Error updating booking total:', updateError);
    throw new Error('Failed to update booking total');
  }

  return discount;
}

async function getBookingDiscounts(bookingId: string) {
  const { data, error } = await supabase
    .from('booking_discounts')
    .select('*')
    .eq('booking_id', bookingId);

  if (error) throw error;
  return data as BookingDiscount[];
}

export async function deleteDiscount(id: string) {
  if (!id) {
    throw new Error('Discount ID is required');
  }

  // First, get the discount details to calculate the amount to add back
  const { data: discount, error: discountError } = await supabase
    .from('booking_discounts')
    .select('*')
    .eq('id', id)
    .single();

  if (discountError) {
    console.error('Error fetching discount:', discountError);
    throw new Error('Failed to fetch discount details');
  }

  // Get the current booking to calculate the new total
  const { data: booking, error: bookingError } = await supabase
    .from('bookings')
    .select('subtotal, tax_amount')
    .eq('id', discount.booking_id)
    .single();

  if (bookingError) {
    console.error('Error fetching booking:', bookingError);
    throw new Error('Failed to fetch booking details');
  }

  // Calculate the discount amount to add back with proper currency rounding
  const discountAmount = Math.round((discount.type === 'percentage'
    ? (booking.subtotal * discount.amount / 100)
    : discount.amount) * 100) / 100;

  // Delete the discount
  const { error: deleteError } = await supabase
    .from('booking_discounts')
    .delete()
    .eq('id', id);

  if (deleteError) {
    console.error('Error deleting discount:', deleteError);
    throw new Error('Failed to delete discount');
  }

  // Update the booking's total amount with proper currency rounding
  const newTotal = Math.round((booking.subtotal + booking.tax_amount) * 100) / 100;
  const { error: updateError } = await supabase
    .from('bookings')
    .update({
      total_amount: newTotal
    })
    .eq('id', discount.booking_id);

  if (updateError) {
    console.error('Error updating booking total:', updateError);
    throw new Error('Failed to update booking total');
  }
}